import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { ATTACHMENT_LINK_TTL_S, isAttachmentLinkRenderable } from '@/lib/attachment-link-lifetime';

export interface TaskAttachment {
  id: string;
  task_id: string;
  filename: string;
  original_filename: string;
  attachment_url: string;
  file_size: number;
  mime_type: string | null;
  file_category: string;
  uploaded_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  metadata: Record<string, unknown> | null;
}

// -- Query key factory --------------------------------------------------------

export const taskAttachmentKeys = {
  byTask: (orgId: string, taskId: string) => ['task-attachments', orgId, taskId] as const,
  byIds: (ids: string[]) => ['attachments-by-ids', ids] as const,
};

// -- Constants ----------------------------------------------------------------

const ATTACHMENT_COLUMNS =
  'id, task_id, filename, original_filename, attachment_url, file_size, mime_type, file_category, uploaded_by, created_at, updated_at, metadata';

const SIGNED_URL_EXPIRY_S = ATTACHMENT_LINK_TTL_S;
const REFRESH_BUFFER_S = 60;
const REFRESH_MS = (SIGNED_URL_EXPIRY_S - REFRESH_BUFFER_S) * 1000;

// -- Helpers ------------------------------------------------------------------

export function extractStoragePath(attachment: { metadata: unknown }): string | null {
  const meta = attachment.metadata as Record<string, unknown> | null;
  return typeof meta?.storage_path === 'string' ? meta.storage_path : null;
}

async function resolveSignedUrls(rows: { metadata: unknown }[]): Promise<Map<string, string>> {
  const paths = rows.map(extractStoragePath).filter((p): p is string => p !== null);
  const unique = [...new Set(paths)];
  if (unique.length === 0) {
    return new Map();
  }

  try {
    const { data: signedUrls, error } = await supabase.storage
      .from('task_attachments')
      .createSignedUrls(unique, SIGNED_URL_EXPIRY_S, { download: true });

    if (error) {
      console.warn('Failed to generate signed URLs for task attachments:', error.message);
      return new Map();
    }

    const urlMap = new Map<string, string>();
    for (const entry of signedUrls ?? []) {
      if (entry.signedUrl && entry.path) {
        urlMap.set(entry.path, entry.signedUrl);
      }
    }
    return urlMap;
  } catch {
    return new Map();
  }
}

function applySignedUrls<T extends { metadata: unknown }>(
  rows: T[],
  urlMap: Map<string, string>,
): TaskAttachment[] {
  return rows.map((row) => {
    const path = extractStoragePath(row);
    return { ...row, attachment_url: (path && urlMap.get(path)) ?? '' } as TaskAttachment;
  });
}

async function fetchAttachmentsWithSignedUrls(taskId: string): Promise<TaskAttachment[]> {
  const { data, error } = await supabase
    .from('task_attachments')
    .select(ATTACHMENT_COLUMNS)
    .eq('task_id', taskId)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }
  if (!data || data.length === 0) {
    return [];
  }

  const urlMap = await resolveSignedUrls(data);
  return applySignedUrls(data, urlMap);
}

// -- Hooks --------------------------------------------------------------------

/** Rows signed too long ago keep their details but offer no link, as on a signing failure. */
function withoutAgedLinks(
  rows: TaskAttachment[] | undefined,
  signedAt: number,
): TaskAttachment[] | undefined {
  if (!rows || signedAt === 0 || isAttachmentLinkRenderable(signedAt)) {
    return rows;
  }
  return rows.map((row) => ({ ...row, attachment_url: '' }));
}

export function useTaskAttachments(taskId: string | undefined, organizationId: string) {
  const query = useQuery({
    queryKey: taskAttachmentKeys.byTask(organizationId, taskId ?? ''),
    queryFn: async () => {
      if (!taskId) {
        throw new Error('taskId is required');
      }
      return await fetchAttachmentsWithSignedUrls(taskId);
    },
    enabled: !!taskId,
    staleTime: REFRESH_MS,
    gcTime: REFRESH_MS + 60_000,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    // The task chip opens its cached link directly, and the app turns mount refetch off.
    refetchOnMount: 'always',
  });
  const data = useMemo(
    () => withoutAgedLinks(query.data, query.dataUpdatedAt),
    [query.data, query.dataUpdatedAt],
  );
  return data === query.data ? query : { ...query, data };
}

export function useAttachmentsByIds(attachmentIds: string[]) {
  return useQuery({
    queryKey: taskAttachmentKeys.byIds(attachmentIds),
    queryFn: async (): Promise<TaskAttachment[]> => {
      if (attachmentIds.length === 0) {
        return [];
      }
      const { data, error } = await supabase
        .from('task_attachments')
        .select(ATTACHMENT_COLUMNS)
        .in('id', attachmentIds)
        .order('created_at', { ascending: false });
      if (error) {
        throw error;
      }
      if (!data || data.length === 0) {
        return [];
      }
      const urlMap = await resolveSignedUrls(data);
      return applySignedUrls(data, urlMap);
    },
    enabled: attachmentIds.length > 0,
    staleTime: REFRESH_MS,
    gcTime: REFRESH_MS + 60_000,
  });
}

// -- V1 compat (deprecated) ---------------------------------------------------

export { formatFileSize } from '@/lib/format-utils';

const EXT_ICONS: Record<string, string> = {
  pdf: '📄',
  jpg: '🖼️',
  jpeg: '🖼️',
  png: '🖼️',
  gif: '🖼️',
  webp: '🖼️',
  doc: '📝',
  docx: '📝',
  xls: '📊',
  xlsx: '📊',
};
const MIME_ICONS: [test: (m: string) => boolean, icon: string][] = [
  [(m) => m.startsWith('image/'), '🖼️'],
  [(m) => m === 'application/pdf', '📄'],
  [(m) => m.includes('document') || m.includes('word'), '📝'],
  [(m) => m.includes('spreadsheet') || m.includes('excel'), '📊'],
  [(m) => m.includes('presentation') || m.includes('powerpoint'), '📊'],
  [(m) => m.startsWith('video/'), '🎥'],
  [(m) => m.startsWith('audio/'), '🎵'],
];

/** @deprecated V1 emoji-based file icon helper */
export function getFileIcon(mimeType: string | null, filename: string): string {
  if (!mimeType) {
    const ext = filename.split('.').pop()?.toLowerCase() ?? '';
    return EXT_ICONS[ext] ?? '📎';
  }
  return MIME_ICONS.find(([test]) => test(mimeType))?.[1] ?? '📎';
}

const FILE_TYPE_MAP: Record<string, string> = {
  pdf: 'pdf',
  doc: 'doc',
  docx: 'docx',
  xls: 'xls',
  xlsx: 'xlsx',
  ppt: 'ppt',
  pptx: 'pptx',
  csv: 'csv',
  jpg: 'jpg',
  jpeg: 'jpeg',
  png: 'png',
  webp: 'webp',
};

export function getFileType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return FILE_TYPE_MAP[ext] ?? 'generic';
}
